/*
 * -----------------------------------------------------------------------
 *  <copyright file="admin/organisations/products/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client/react';

import { useBoolean, useAuthorization, useTranslation } from 'hooks';
import { OrganisationProductFields, StudyFields } from 'queries/generated/fragments.generated';
import { GET_ORGANISATION_PROGRAM_PRODUCT } from 'queries/uam';

import './styles.scss';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import NotificationService from 'utils/notification';
import AddProductDialog from 'views/components/admin/add-organisation-product-dialog';
import ProductsGrid from 'views/components/admin/organisations/products/products-grid';
import Button from 'views/components/core/buttons/button';
import Loading from 'views/components/core/loading';
import PageTitle from 'views/components/core/page-title/page-title';
import Error from 'views/components/error/error';

interface AdminOrganisationProductsInterface {
    study: StudyFields;
}

const AdminOrganisationProducts = ({ study }: AdminOrganisationProductsInterface) => {
    const { t } = useTranslation();
    const user = useAuthorization();
    const [currentProduct, setCurrentProduct] = useState(null);
    const showAddProductDialog = useBoolean(false);
    const { data, loading, error } = useQuery(GET_ORGANISATION_PROGRAM_PRODUCT);
    const canEdit = user?.canWriteSystemAdminLevel();
    const [products, setProducts] = useState([]);

    useEffect(() => {
        setProducts(restructuredUpdatedCreatedBy(data?.organisation?.products));
    }, [data]);

    const openAddProductDialog = () => {
        showAddProductDialog.setTrue();
    };

    const actions = (item: OrganisationProductFields) => [
        {
            label: 'admin:editProductLabel',
            icon: ['fal', 'edit'],
            onClick: () => {
                setCurrentProduct(item);
                showAddProductDialog.setTrue();
            },
            enabled: canEdit,
        },
    ];

    if (loading) return <Loading float />;

    if (error) return <Error error={error} />;

    return (
        <>
            <AddProductDialog
                currentProduct={currentProduct}
                organisation={data?.organisation}
                open={showAddProductDialog.value}
                close={(action: string) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'add') NotificationService.showNotification(t('admin:productSuccessfulCreate'));
                        if (action === 'edit')
                            NotificationService.showNotification(
                                t('admin:productSuccessfulUpdated', {
                                    id: currentProduct.recordId,
                                }),
                            );

                        setCurrentProduct(null);
                        showAddProductDialog.setFalse();
                    }
                }}
            />
            <PageTitle text={t('admin:manageProducts')} />
            <div className="btn-wrapper-create-product top-title-btn-wrapper">
                <Button
                    className="product-add-btn main-button"
                    text="admin:addProduct"
                    onClick={openAddProductDialog}
                    disabled={!canEdit}
                />
            </div>
            <ProductsGrid products={products} actions={actions} />
        </>
    );
};

export default AdminOrganisationProducts;
